import { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import api, { photoUrl } from "@/api";
import { useI18n } from "@/i18n";
import { askConfirm } from "@/components/ConfirmHost";
import { toast } from "sonner";
import { CopyX, Loader2, X, Trash2, RefreshCw, Check } from "lucide-react";

const ROUTE_ENTITY = [
  ["/app/partners", "customers"], ["/app/suppliers", "suppliers"],
  ["/app/recipes", "recipes"], ["/app/ingredients-list", "recipes"], ["/app/catalog", "recipes"],
  ["/app/listino", "recipes"], ["/app/foodcost", "recipes"], ["/app/caldane", "recipes"],
  ["/app/inventory", "ingredients"], ["/app/inventario", "ingredients"],
  ["/app/cassa", "pos"], ["/app/formula", "formula"], ["/app/foto", "photos"],
  ["/app/orders", "orders"], ["/app/production", "orders"],
];

export const DedupScanner = () => {
  const { t, lang } = useI18n();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [keep, setKeep] = useState({});
  const entity = (ROUTE_ENTITY.find(([p]) => loc.pathname.startsWith(p)) || [null, "all"])[1];

  const entLabel = (k) => ({ customers: t("customersTab"), suppliers: t("suppliersTab"), recipes: t("recipes"),
    ingredients: t("inventory"), pos: t("posTitle"), formula: t("formulaTitle"), photos: t("photosLbl"), orders: t("orders") }[k] || k);

  const scan = async (ent) => {
    setBusy(true); setRes(null); setKeep({});
    try {
      const r = await api.dedupScan(ent);
      const k = {};
      Object.entries(r.results || {}).forEach(([e, groups]) => groups.forEach((g, gi) => { g.uid = `${e}:${gi}:${g.items[0].id}`; k[g.uid] = g.items[0].id; }));
      setRes({ ent, ...r });
      setKeep(k);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const start = () => { setOpen(true); scan(entity); };

  const dropGroup = (ent, uid) => setRes((prev) => {
    if (!prev) return prev;
    const results = { ...prev.results };
    const groups = (results[ent] || []).filter((g) => g.uid !== uid);
    if (groups.length) results[ent] = groups; else delete results[ent];
    return { ...prev, results, total_groups: Math.max(0, (prev.total_groups || 1) - 1) };
  });

  const resolveGroup = async (ent, g) => {
    const keepId = keep[g.uid] || g.items[0].id;
    const removeIds = g.items.filter((it) => it.id !== keepId).map((it) => it.id);
    if (!removeIds.length) return;
    if (!(await askConfirm(t("dedupConfirm").replace("{n}", removeIds.length)))) return;
    try {
      const r = await api.dedupResolve(ent, removeIds, keepId);
      toast.success(`${t("dedupDeleted")} (${r.removed})`);
      dropGroup(ent, g.uid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const markDifferent = async (ent, g) => {
    try {
      await api.dedupIgnore(ent, g.items.map((it) => it.id));
      toast.success(t("dedupNotDupSaved"));
      dropGroup(ent, g.uid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const reasonLbl = { name: t("dedupReasonName"), similar: t("dedupReasonSimilar"), photo: t("dedupReasonPhoto") };
  const reasonCls = { name: "bg-red-100 text-red-700", similar: "bg-amber-100 text-amber-700", photo: "bg-fuchsia-100 text-fuchsia-700" };

  return (
    <>
      <button onClick={start} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title={t("dedupBtn")} data-testid="dedup-scan-btn">
        <CopyX className="w-4 h-4 text-white/70" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[90] bg-black/55 flex items-center justify-center p-3" onClick={() => setOpen(false)} data-testid="dedup-modal">
          <div className="bg-card text-foreground rounded-2xl border-2 border-border shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
              <CopyX className="w-5 h-5 text-accent shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold">{t("dedupTitle")}</div>
                <div className="text-xs text-muted-foreground">{res?.ent === "all" ? t("dedupScanAll") : entLabel(res?.ent || entity)}</div>
              </div>
              {res && res.ent !== "all" && (
                <button onClick={() => scan("all")} className="btn-ghost text-xs border border-border" data-testid="dedup-scan-all-btn">🌍 {t("dedupScanAll")}</button>
              )}
              <button onClick={() => scan(res?.ent || entity)} className="btn-ghost text-xs border border-border" title={t("dedupRescan")} data-testid="dedup-rescan-btn"><RefreshCw className="w-3.5 h-3.5" /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-muted" data-testid="dedup-close-btn"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {busy && <div className="flex items-center gap-2 justify-center py-12 text-sm text-muted-foreground" data-testid="dedup-loading"><Loader2 className="w-5 h-5 animate-spin" /> {t("dedupScanning")}</div>}
              {!busy && res && res.total_groups === 0 && <div className="text-center py-12 text-sm text-muted-foreground" data-testid="dedup-empty">{t("dedupNone")}</div>}
              {!busy && res && Object.entries(res.results || {}).map(([ent, groups]) => (
                <div key={ent} className="mb-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{entLabel(ent)} · {groups.length} {t("dedupGroupsFound")}</div>
                  {groups.map((g, gi) => (
                    <div key={g.uid} className="border border-border rounded-xl p-3 mb-3" data-testid={`dedup-group-${ent}-${gi}`}>
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <span className={`pill text-[10px] font-bold ${reasonCls[g.reason] || reasonCls.name}`}>{reasonLbl[g.reason] || g.reason}</span>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => markDifferent(ent, g)} className="btn-ghost text-xs border border-emerald-300 text-emerald-700" data-testid={`dedup-ok-${ent}-${gi}`}>
                            <Check className="w-3.5 h-3.5" /> {t("dedupNotDup")}
                          </button>
                          <button onClick={() => resolveGroup(ent, g)} className="btn-ghost text-xs border border-red-300 text-red-700" data-testid={`dedup-del-${ent}-${gi}`}>
                            <Trash2 className="w-3.5 h-3.5" /> {t("dedupDeleteGroup")}
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {g.items.map((it) => (
                          <label key={it.id} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer border ${keep[g.uid] === it.id ? "border-emerald-400 bg-emerald-50" : "border-transparent bg-muted/40"}`} data-testid={`dedup-item-${it.id}`}>
                            <input type="radio" name={`keep-${g.uid}`} checked={keep[g.uid] === it.id}
                              onChange={() => setKeep((k) => ({ ...k, [g.uid]: it.id }))} className="accent-emerald-600" />
                            {it.photo_path && <img src={photoUrl(it.photo_path)} alt="" className="w-9 h-9 rounded-lg object-cover border border-border shrink-0" />}
                            <span className="flex-1 min-w-0 text-sm">
                              <b className="block truncate">{it.name || "—"}</b>
                              <span className="text-[11px] text-muted-foreground truncate block">
                                {Object.entries(it.extra || {}).map(([k2, v]) => `${k2}: ${v}`).join(" · ")}
                                {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}` : ""}
                              </span>
                            </span>
                            {keep[g.uid] === it.id && <span className="pill text-[10px] bg-emerald-100 text-emerald-700 font-bold shrink-0">✓ {t("dedupKeep")}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-border text-[11px] text-muted-foreground">{t("dedupTrashHint")}</div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
